import {
  LoginOutlined,
  LogoutOutlined,
  MoneyCollectOutlined,
  SettingOutlined,
  UserOutlined,
  VideoCameraOutlined,
  PieChartOutlined,
} from '@ant-design/icons';
import { Avatar, Button, Layout, Menu, Row, Skeleton, Space, Tag, Typography } from 'antd';
import React, { ReactNode, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { signOut } from 'src/features/auth/auth.state';
import { RoleEnum, User } from 'src/features/users/users.types';
import { RootState } from 'src/store';
import styles from './MainLayout.module.css';
import { version } from '../../../../package.json';
import MenuItemProtected from 'src/components/blocks/MenuItemProtected/MenuItemProtected';
import SubMenuProtected from 'src/components/blocks/SubMenuProtected/SubMenuProtected';

const { Sider, Content } = Layout;

export interface MainLayoutProps {
  children: ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = props => {
  const { children } = props;

  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();

  const user = useSelector<RootState, User | null>(state => state.auth.user);
  const authManager = useSelector<RootState, RootState['auth']['manager']>(state => state.auth.manager);

  const handleLogoutButtonClick = useCallback(async () => {
    await dispatch(signOut());
    history.push(`/login`);
  }, [history, dispatch]);

  return (
    <Layout className={styles.root}>
      <Layout hasSider>
        <Sider theme='light' className={styles.sider} width={300}>
          <Row align='middle'>
            {authManager.isPending && (
              <Space>
                <Skeleton.Avatar active size='large' shape='circle' style={{ verticalAlign: 'middle' }} />
                <Skeleton.Button active size='small' shape='square' style={{ verticalAlign: 'middle' }} />
              </Space>
            )}
            {user && (
              <div className={styles.userPanelWrap}>
                <Avatar size='large' shape='circle' className={styles.avatar}>
                  <UserOutlined />
                </Avatar>
                <div className={styles.emailWrap}>
                  <span className={styles.email}>{user.email}</span>
                  {user.admin && (
                    <Tag color='red' className={styles.adminTag}>
                      admin
                    </Tag>
                  )}
                </div>
                <Button
                  size='middle'
                  style={{
                    verticalAlign: 'middle',
                  }}
                  onClick={handleLogoutButtonClick}
                  className={styles.signInButton}
                >
                  <LogoutOutlined />
                  Выйти
                </Button>
                <Typography.Text className={styles.versionLabel}>
                  Версия:&nbsp;{version}-{process.env.REACT_APP_GIT_SHA}
                </Typography.Text>
              </div>
            )}
            {!user && !authManager.isPending && (
              <Button
                type='primary'
                size='small'
                style={{
                  verticalAlign: 'middle',
                }}
              >
                <Link to={'/login'}>
                  <Space>
                    <LoginOutlined />
                    Войти
                  </Space>
                </Link>
              </Button>
            )}
          </Row>
          <Menu
            mode='inline'
            theme='light'
            selectedKeys={[location.pathname]}
            defaultOpenKeys={['commercial', 'content', 'settings', 'testing']}
          >
            <SubMenuProtected
              key='commercial'
              icon={<MoneyCollectOutlined />}
              title='Коммерция'
              roles={[RoleEnum.admin, RoleEnum.observer, RoleEnum.companyContentManager]}
            >
              <Menu.Item key='/commercial/companies'>
                <Link to={'/commercial/companies'}>Компании</Link>
              </Menu.Item>
              <MenuItemProtected key='/commercial/accounts' roles={[RoleEnum.admin, RoleEnum.observer]}>
                <Link to={'/commercial/accounts'}>Аккаунты</Link>
              </MenuItemProtected>
            </SubMenuProtected>
            <SubMenuProtected
              key='content'
              icon={<VideoCameraOutlined />}
              title='Контент'
              roles={[RoleEnum.admin, RoleEnum.observer, RoleEnum.contentManager]}
            >
              <Menu.Item key='/content/cards'>
                <Link to={'/content/cards'}>Карточки контента</Link>
              </Menu.Item>
              <Menu.Item key='/content/templates'>
                <Link to={'/content/templates'}>Шаблоны</Link>
              </Menu.Item>
              <Menu.Item key='/content/collections'>
                <Link to={'/content/collections'}>Коллекции</Link>
              </Menu.Item>
              <Menu.Item key='/content/accounts'>
                <Link to={'/content/accounts'}>Пользователи</Link>
              </Menu.Item>
            </SubMenuProtected>
            <SubMenuProtected
              key='testing'
              icon={<PieChartOutlined />}
              title='Тестирование'
              roles={[RoleEnum.admin, RoleEnum.observer]}
            >
              <Menu.Item key='/testing/groups'>
                <Link to={'/testing/groups'}>Группы устройств</Link>
              </Menu.Item>
            </SubMenuProtected>
            <SubMenuProtected
              key='settings'
              icon={<SettingOutlined />}
              title='Настройки'
              roles={[RoleEnum.admin, RoleEnum.observer]}
            >
              <Menu.Item key='/settings/devices'>
                <Link to={'/settings/devices'}>Устройства</Link>
              </Menu.Item>
              <Menu.Item key='/settings/users'>
                <Link to={'/settings/users'}>Пользователи CMS</Link>
              </Menu.Item>
              <Menu.Item key='/settings/device-types'>
                <Link to={'/settings/device-types'}>Типы устройств</Link>
              </Menu.Item>
              <Menu.Item key='/settings/firmwares'>
                <Link to={'/settings/firmwares'}>Прошивки</Link>
              </Menu.Item>
              <Menu.Item key='/settings/languages'>
                <Link to={'/settings/languages'}>Языки</Link>
              </Menu.Item>
              <Menu.Item key='/settings/locations'>
                <Link to={'/settings/locations'}>Локации</Link>
              </Menu.Item>
            </SubMenuProtected>
          </Menu>
        </Sider>
        <Content className={styles.content}>{children}</Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout;
